import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { getTheme, ThemeMode } from './theme';

interface ThemeContextType {
  mode: ThemeMode;
  toggleTheme: () => void;
  setTheme: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
  defaultMode?: ThemeMode;
  mode?: ThemeMode; // controlled mode
}

// Theme persistence helper
const getStoredTheme = (): ThemeMode => {
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem('unicity-theme');
    if (stored && ['light', 'dark', 'neumorphism-light', 'neumorphism-dark'].includes(stored)) {
      return stored as ThemeMode;
    }
  }
  return 'light';
};

const setStoredTheme = (theme: ThemeMode) => {
  if (typeof window !== 'undefined') {
    localStorage.setItem('unicity-theme', theme);
  }
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  defaultMode,
  mode: controlledMode,
}) => {
  const [mode, setMode] = useState<ThemeMode>(() => {
    return getStoredTheme() || defaultMode || 'light';
  });

  // Use controlled mode if provided, otherwise use internal state
  const effectiveMode = controlledMode !== undefined ? controlledMode : mode;

  // Only persist if not controlled
  useEffect(() => {
    if (controlledMode === undefined) {
      setStoredTheme(mode);
    }
  }, [mode, controlledMode]);

  const toggleTheme = () => {
    setMode((prevMode) => {
      switch (prevMode) {
        case 'light':
          return 'dark';
        case 'dark':
          return 'neumorphism-light';
        case 'neumorphism-light':
          return 'neumorphism-dark';
        case 'neumorphism-dark':
          return 'light';
        default:
          return 'light';
      }
    });
  };

  const setTheme = (newMode: ThemeMode) => {
    setMode(newMode);
  };

  const theme = getTheme(effectiveMode);

  return (
    <ThemeContext.Provider value={{ mode: effectiveMode, toggleTheme, setTheme }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}; 